import React from "react";
import {
  Button,
  Card,
  Col,
  Row,
  Badge,
  Spinner,
  Modal,
  Form,
  Table,
} from "@themesberg/react-bootstrap";
import { useState, useCallback, useEffect } from "react";
import { postApi, createTokenPin } from "../../../redux/api";
import CustomTable from "../../../components/CustomTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckDouble,
  faEye,
  faSync,
  faUndo,
  faClipboardCheck,
  faUniversity,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import { formatNumber } from "../../../redux/utils";
import { useSelector } from "react-redux";
import moment from "moment";
import toast from "react-hot-toast";

const BANK_CODE_MAP = {
  "Access Bank": "044",
  "Access Bank Plc": "044",
  "Citibank": "023",
  "Citibank Nigeria": "023",
  "Ecobank": "050",
  "Ecobank Nigeria": "050",
  "Fidelity Bank": "070",
  "Fidelity Bank Plc": "070",
  "First Bank": "011",
  "First Bank of Nigeria": "011",
  "First Bank Plc": "011",
  "FCMB": "214",
  "First City Monument Bank": "214",
  "GTBank": "058",
  "GTBank Plc": "058",
  "Guaranty Trust Bank": "058",
  "GTCO": "058",
  "Heritage Bank": "030",
  "Jaiz Bank": "301",
  "Keystone Bank": "082",
  "Polaris Bank": "076",
  "Premium Trust Bank": "173",
  "Providus Bank": "101",
  "Stanbic IBTC": "221",
  "Stanbic IBTC Bank": "221",
  "Standard Chartered": "068",
  "Sterling Bank": "232",
  "SunTrust Bank": "100",
  "Titan Trust Bank": "102",
  "UBA": "033",
  "United Bank for Africa": "033",
  "Union Bank": "032",
  "Union Bank of Nigeria": "032",
  "Unity Bank": "215",
  "Wema Bank": "035",
  "Zenith Bank": "057",
  "Zenith Bank Plc": "057",
  "Globus Bank": "001",
  "LAPO Microfinance": "177",
  "Moniepoint": "505",
  "Moniepoint MFB": "505",
  "Opay": "999",
  "PalmPay": "107",
  "Kuda Bank": "502",
  "Kuda Microfinance": "502",
  "VFD": "110",
  "VFD Microfinance": "110",
  "AB Microfinance": "804",
  "Accion Microfinance": "806",
  "ALAT by Wema": "035",
  "Carbon": "502",
  "Fairmoney": "513",
  "Rubies MFB": "125",
  "Sparkle": "513",
  "TAJ Bank": "302",
  "Zenith": "057",
};

const resolveBankCode = (bankName) => {
  if (!bankName) return "";
  const trimmed = bankName.trim();
  return BANK_CODE_MAP[trimmed] || BANK_CODE_MAP[trimmed.replace(/\s+/g, " ")] || "";
};

function CashOfficeApprovePayments() {
  const user = useSelector((state) => state.auth.user);

  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const [sourceAccount, setSourceAccount] = useState(null);
  const [accountLoading, setAccountLoading] = useState(false);
  const [pin, setPin] = useState("");

  const fetchBatches = useCallback(async () => {
    try {
      setLoading(true);
      const data = await postApi("get_cash_office_batches", {
        form: { query_type: "reviewed" },
      });
      if (data?.result?.length) {
        setBatches(data.result);
      } else {
        setBatches([]);
      }
    } catch (err) {
      console.error("Error fetching approve batches:", err);
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  const fetchSourceAccount = useCallback(async () => {
    if (!user?.mda_code) return;
    setAccountLoading(true);
    const data = await postApi("get-mda-account", { mda_code: user.mda_code });
    if (data?.success && data?.result) {
      setSourceAccount(data.result);
    }
    setAccountLoading(false);
  }, [user?.mda_code]);

  useEffect(() => {
    fetchSourceAccount();
  }, [fetchSourceAccount]);

  const viewDetail = (batch) => {
    setSelectedBatch(batch);
    setShowDetail(true);
  };

  const returnBatch = async (batchNumber) => {
    setProcessing(true);
    try {
      const data = await postApi("update_cash_office_batch", {
        batch_number: batchNumber,
        status: "returned",
        userId: user?.username,
      });
      if (data?.success) {
        toast.success(`Batch ${batchNumber} returned.`);
        setShowDetail(false);
        setSelectedBatch(null);
        fetchBatches();
      } else {
        toast.error(data?.message || "Failed to return batch");
      }
    } catch (err) {
      toast.error("Failed to return batch. Please try again.");
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  const approveAndPayWithRemita = async () => {
    if (!pin || pin.length < 4) {
      toast.error("Please enter your 4-digit PIN");
      return;
    }
    const pvs = selectedBatch?.pvs || [];
    if (!pvs.length || !sourceAccount) {
      toast.error("Missing payment data");
      return;
    }

    setProcessing(true);

    const sourceForm = {
      account_number: sourceAccount.account_no,
      bank_code: resolveBankCode(sourceAccount.bank_name),
      account_name: sourceAccount.account_name,
      account_type: sourceAccount.account_type || "Default",
    };

    const vPinToken = createTokenPin({ vPinToken: pin });

    try {
      let result;

      if (pvs.length === 1) {
        const pv = pvs[0];
        result = await postApi("remita/single-transfer", {
          vPinToken,
          pin,
          amount: parseFloat(pv.amount),
          ben_account_no: pv.contractor_acc_no,
          ben_bank_cbn_code: resolveBankCode(pv.contractor_bank_name),
          narration: pv.narration || `Payment for ${pv.pv_code}`,
          payment_ref: `COB_${selectedBatch.batch_number}_${pv.pv_code}`,
          form: sourceForm,
        });
      } else {
        const details = pvs.map((pv) => ({
          amount: parseFloat(pv.amount),
          ben_account_no: pv.contractor_acc_no,
          ben_bank_cbn_code: resolveBankCode(pv.contractor_bank_name),
          narration: pv.narration || `Payment for ${pv.pv_code}`,
          beneficiary_name: pv.contractor_name || "",
          payment_ref: `COB_${selectedBatch.batch_number}_${pv.pv_code}_${Date.now()}`,
        }));

        result = await postApi("remita/transfer", {
          vPinToken,
          pin,
          details,
          batch_reference: `COB_${selectedBatch.batch_number}`,
          batch_description: `Cash Office Batch ${selectedBatch.batch_number} - ${pvs.length} payments`,
          type: "approval",
          form: sourceForm,
        });
      }

      if (result?.success) {
        await postApi("update_cash_office_batch", {
          batch_number: selectedBatch.batch_number,
          status: "paid",
          userId: user?.username,
        });
        toast.success(`Payment completed for batch ${selectedBatch.batch_number}!`);
        setShowDetail(false);
        setSelectedBatch(null);
        setPin("");
        fetchBatches();
      } else {
        toast.error(result?.message || "Payment failed via Remita");
      }
    } catch (err) {
      toast.error(err?.message || "Payment failed. Please try again.");
      console.error("Remita payment error:", err);
    } finally {
      setProcessing(false);
    }
  };

  const batchFields = [
    { title: "Batch No", value: "batch_number" },
    {
      title: "No. of PVs",
      custom: true,
      component: (item) => (
        <span className="text-center d-block">{item.no_of_payments}</span>
      ),
    },
    {
      title: "Total Amount (₦)",
      tableHeadStyle: { textAlign: "right" },
      custom: true,
      component: (item) => (
        <div className="text-end">{formatNumber(item.total_amount)}</div>
      ),
    },
    {
      title: "Created Date",
      custom: true,
      component: (item) => (
        <span>{moment(item.created_date).format("DD/MM/YYYY")}</span>
      ),
    },
    {
      title: "Action",
      custom: true,
      component: (item) => (
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => viewDetail(item)}
        >
          <FontAwesomeIcon icon={faEye} className="me-1" />
          View & Approve
        </Button>
      ),
    },
  ];

  const pvDetailFields = [
    { title: "PV Code", value: "pv_code" },
    { title: "MDA Name", value: "mda_name" },
    {
      title: "Amount (₦)",
      tableHeadStyle: { textAlign: "right" },
      custom: true,
      component: (item) => (
        <div className="text-end">{formatNumber(item.amount)}</div>
      ),
    },
    { title: "Contractor", value: "contractor_name" },
    { title: "Bank", value: "contractor_bank_name" },
    { title: "Account Name", value: "contractor_acc_name" },
    { title: "Account No", value: "contractor_acc_no" },
    { title: "TIN", value: "contractor_tin" },
    { title: "Sort Code", value: "sort_code" },
  ];

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div>
          <h4>
            <FontAwesomeIcon icon={faClipboardCheck} className="me-2" />
            Approve Payments — Cash Office
          </h4>
        </div>
        <Button variant="outline-primary" size="sm" onClick={fetchBatches}>
          <FontAwesomeIcon icon={faSync} className="me-1" />
          Refresh
        </Button>
      </div>

      <Card className="mb-4">
        <Card.Body>
          <h6 className="mb-3">
            <FontAwesomeIcon icon={faUniversity} className="me-2" />
            Source Account
          </h6>
          {accountLoading ? (
            <Spinner animation="border" size="sm" />
          ) : sourceAccount ? (
            <Row className="g-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small text-muted mb-0">Account Name</Form.Label>
                  <Form.Control size="sm" value={sourceAccount.account_name || ""} disabled />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small text-muted mb-0">Bank Name</Form.Label>
                  <Form.Control size="sm" value={sourceAccount.bank_name || ""} disabled />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small text-muted mb-0">Account No</Form.Label>
                  <Form.Control size="sm" value={sourceAccount.account_no || ""} disabled />
                </Form.Group>
              </Col>
            </Row>
          ) : (
            <p className="text-muted small mb-0">Source account not found</p>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading batches for approval...</p>
            </div>
          ) : batches.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <FontAwesomeIcon icon={faClipboardCheck} size="3x" className="mb-3" />
              <h5>No batches pending approval</h5>
              <p>All reviewed batches have been approved or none are ready yet.</p>
            </div>
          ) : (
            <CustomTable
              fields={batchFields}
              data={batches}
              noSort={false}
              hover={true}
            />
          )}
        </Card.Body>
      </Card>

      <Modal
        show={showDetail}
        onHide={() => { setShowDetail(false); setSelectedBatch(null); }}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Batch: {selectedBatch?.batch_number}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBatch && (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <strong>Batch No:</strong> {selectedBatch.batch_number}
                </Col>
                <Col md={4}>
                  <strong>No. of PVs:</strong> {selectedBatch.no_of_payments}
                </Col>
                <Col md={4}>
                  <strong>Total Amount:</strong> ₦
                  {formatNumber(selectedBatch.total_amount)}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={4}>
                  <strong>Created:</strong>{" "}
                  {moment(selectedBatch.created_date).format("DD/MM/YYYY")}
                </Col>
                <Col md={4}>
                  <strong>Status:</strong>{" "}
                  <Badge bg="warning">{selectedBatch.status}</Badge>
                </Col>
              </Row>

              {sourceAccount && (
                <Card className="mb-3 border-primary">
                  <Card.Header className="bg-primary text-white py-2">
                    <FontAwesomeIcon icon={faUniversity} className="me-1" />
                    Source Account (Debit From)
                  </Card.Header>
                  <Card.Body className="py-2">
                    <Row>
                      <Col md={4}><strong>Account Name:</strong> <p className="mb-0">{sourceAccount.account_name || "—"}</p></Col>
                      <Col md={4}><strong>Bank:</strong> <p className="mb-0">{sourceAccount.bank_name || "—"}</p></Col>
                      <Col md={4}><strong>Account No:</strong> <p className="mb-0">{sourceAccount.account_no || "—"}</p></Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}

              <Card className="mb-3 border-success">
                <Card.Header className="bg-success text-white py-2">
                  <FontAwesomeIcon icon={faCheckDouble} className="me-1" />
                  {selectedBatch.pvs.length === 1 ? "Beneficiary Account (Credit To)" : `Beneficiary Accounts (${selectedBatch.pvs.length} recipients)`}
                </Card.Header>
                <Card.Body className="py-2">
                  {selectedBatch.pvs.length === 1 ? (
                    <Row>
                      <Col md={4}><strong>Contractor:</strong> <p className="mb-0">{selectedBatch.pvs[0].contractor_name}</p></Col>
                      <Col md={4}><strong>Bank:</strong> <p className="mb-0">{selectedBatch.pvs[0].contractor_bank_name}</p></Col>
                      <Col md={4}><strong>Account No:</strong> <p className="mb-0">{selectedBatch.pvs[0].contractor_acc_no}</p></Col>
                      <Col md={4} className="mt-2"><strong>Account Name:</strong> <p className="mb-0">{selectedBatch.pvs[0].contractor_acc_name}</p></Col>
                      <Col md={4} className="mt-2"><strong>Amount:</strong> <p className="mb-0">₦{formatNumber(selectedBatch.pvs[0].amount)}</p></Col>
                    </Row>
                  ) : (
                    <Table striped size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Contractor</th>
                          <th>Bank</th>
                          <th>Account Name</th>
                          <th>Account No</th>
                          <th>Amount (₦)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedBatch.pvs.map((pv, i) => (
                          <tr key={pv.pv_code}>
                            <td>{i + 1}</td>
                            <td>{pv.contractor_name}</td>
                            <td>{pv.contractor_bank_name}</td>
                            <td>{pv.contractor_acc_name}</td>
                            <td>{pv.contractor_acc_no}</td>
                            <td className="text-end">{formatNumber(pv.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>

              <div className="text-center mt-3">
                <Form.Label className="fw-semibold">
                  <FontAwesomeIcon icon={faLock} className="me-1" />
                  Enter PIN to Confirm Payment
                </Form.Label>
                <div>
                  <Form.Control
                    type="password"
                    maxLength={4}
                    placeholder="Enter 4-digit PIN"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    className="text-center d-inline-block"
                    style={{ maxWidth: 200, letterSpacing: "0.3em", fontSize: "1.2em" }}
                    disabled={processing}
                  />
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            onClick={() => returnBatch(selectedBatch?.batch_number)}
            disabled={processing}
          >
            {processing ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <FontAwesomeIcon icon={faUndo} className="me-1" />
            )}
            Return Batch
          </Button>
          <Button
            variant="success"
            onClick={approveAndPayWithRemita}
            disabled={processing || pin.length < 4}
          >
            {processing ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <FontAwesomeIcon icon={faCheckDouble} className="me-1" />
            )}
            {processing ? "Processing..." : "Approve & Make Payment"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default CashOfficeApprovePayments;
